// The owner's say over a share, each a `control` frame or a call to the REST
// API. On the share's page each sits where it acts (byj-1wv.51.3): pause or
// resume and stop — once it has ended, delete — on a bar under the page's
// head, always in view, with the share's state; remove and the keyboard on
// the people they act on, with Invite over them (components/InviteLink); the policy
// folded under them, saying what is on; the turns coaches asked for under
// it. There is no permission approval here: the owner answers those at the
// terminal.
import { useNow } from "../now";
import { api } from "../api";
import { canonicalSource, type Caps, type ControlBody, type Policy } from "../protocol";
import { t, when, word, phrases } from "../i18n";
import type { SeqEvent } from "../timeline";
import { Badge, type Names } from "./Badge";
import { useConfirm } from "./Confirm";
import { InviteLinkPopover, type MadeInvite } from "./InviteLink";
import { FoldPanel } from "./SidePanel";
import { StateBar } from "./StateBar";

export type { MadeInvite };

/**
 * The owner's bar under the page's head, in view whether the side panel is
 * open, folded or, on a phone, under the main pane: whether the share is
 * live, paused or over, who is in it and for how long, whether it is being
 * recorded; then, while it is live, pause or resume it and stop it (asked
 * first), not while the page is not connected, and once it has ended,
 * delete it (asked first) when it may be deleted.
 */
export function ShareBar({ ended, paused, recording, people, startedAt, disabled, onControl, onDelete }: {
  ended: boolean;
  paused: boolean;
  recording: boolean;
  /** who is in it, as the relay last said */
  people: number;
  startedAt?: string;
  disabled: boolean;
  onControl: (c: ControlBody) => void;
  onDelete?: () => void;
}) {
  const [confirm, dialog] = useConfirm();
  // how long it has run, said again while it does (now.ts)
  const now = useNow(!ended);
  const state = ended ? "ended" : paused ? "paused" : "live";
  return (
    <StateBar
      label={t("session:ownerControls")}
      state={state}
      stateLabel={<span className="share-bar-state">{word("session:shareBarState", state)}</span>}
      actions={
        ended ? (
          onDelete && (
            <button className="small danger" onClick={onDelete}>
              {t("session:deleteShareLong")}
            </button>
          )
        ) : (
          <>
            <button className="small" disabled={disabled} onClick={() => onControl({ action: paused ? "resume" : "pause" })}>
              {paused ? t("session:resumeSharing") : t("session:pauseSharing")}
            </button>
            <button
              className="small danger"
              disabled={disabled}
              onClick={async () => {
                if (await confirm(t("session:stopConfirm"), { action: t("session:stopSharing"), danger: true })) onControl({ action: "stop" });
              }}
            >
              {t("session:stopSharing")}
            </button>
          </>
        )
      }
    >
      {dialog}
      {!ended && (
        <span className="share-bar-facts">
          {/* nobody until the relay's first presence, which counts the owner too */}
          {[people > 0 ? t("shares:participants", { count: people }) : null, startedAt ? phrases.elapsed(now - Date.parse(startedAt)) : null].filter(Boolean).join(" · ")}
        </span>
      )}
      {!ended && recording && <span className="chip share-bar-rec">{t("session:shareBarRecording")}</span>}
    </StateBar>
  );
}

/** What the policy has on, in a word each, for its folded line. */
export function policyOn(caps: Caps, p: Policy): string[] {
  return [
    p.notes && t("session:policyWord.notes"),
    caps.inject && p.inject && t("session:policyWord.inject"),
    caps.inject && p.inject && p.consent && t("session:policyWord.consent"),
    caps.terminal && p.chat && t("session:policyWord.chat"),
    p.record && t("session:policyWord.record"),
    caps.agent && p.keep_questions && t("session:policyWord.keepQuestions"),
  ].filter((x): x is string => typeof x === "string");
}

/** The share's policy, folded to what is on; open, its switches — only those its session can act on. */
export function PolicyPanel({ source, caps, policy, onControl }: {
  source: string;
  caps: Caps;
  policy: Policy;
  onControl: (c: ControlBody) => void;
}) {
  const setPolicy = (patch: Partial<Policy>) => onControl({ action: "policy", policy: { ...policy, ...patch } });
  const on = policyOn(caps, policy);
  return (
    <FoldPanel
      label={t("session:policy")}
      summary={
        // what is on, a badge each, readable while it is folded
        on.length ? (
          on.map((w) => (
            <span key={w} className="chip chip-on">
              {w}
            </span>
          ))
        ) : (
          <span className="muted small">{t("session:policyAllOff")}</span>
        )
      }
    >
      <label className="toggle">
        <input type="checkbox" checked={policy.notes} onChange={(e) => setPolicy({ notes: e.target.checked })} />
        {t("session:policyNotes")}
      </label>
      {caps.inject ? (
        <>
          <label className="toggle">
            <input type="checkbox" checked={policy.inject} onChange={(e) => setPolicy({ inject: e.target.checked })} />
            {t("session:policyInject")}
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={policy.consent}
              disabled={!policy.inject}
              onChange={(e) => setPolicy({ consent: e.target.checked })}
            />
            {t("session:policyConsent")}
          </label>
        </>
      ) : caps.terminal ? (
        // a terminal takes no injected turns; its one more switch is whether
        // everyone invited may chat (RFC-043 model 21)
        <label className="toggle">
          <input type="checkbox" checked={policy.chat === true} onChange={(e) => setPolicy({ chat: e.target.checked })} />
          {t("session:policyChat")}
        </label>
      ) : (
        // said of the kind that never takes them; an agent is asked, not
        // given turns, and says nothing here
        canonicalSource(source) === "claude" && <p className="muted small">{t("session:policyInjectClaude")}</p>
      )}
      <label className="toggle">
        <input type="checkbox" checked={policy.record === true} onChange={(e) => setPolicy({ record: e.target.checked })} />
        {t("session:policyRecord")}
      </label>
      {caps.agent && (
        // the callers' questions kept for the owner to see and make examples
        // of, callers told before they ask (byj-1wv.48)
        <label className="toggle">
          <input type="checkbox" checked={policy.keep_questions === true} onChange={(e) => setPolicy({ keep_questions: e.target.checked })} />
          {t("session:policyKeepQuestions")}
        </label>
      )}
    </FoldPanel>
  );
}

/** The turns coaches asked the owner to run, oldest first: shown once there is one. */
export function RequestedTurns({ injects, names }: { injects: SeqEvent[]; names: Names }) {
  if (injects.length === 0) return null;
  return (
    <section className="panel" aria-label={t("session:requestedTurns")}>
      <h3>{t("session:requestedTurns")}</h3>
      <ul className="history">
        {injects.map((e) => (
          <li key={e.seq}>
            <Badge actor={e.body.by} names={names} /> <span className="muted small">{when(e.ts)}</span>
            <p>{e.body.text ?? ""}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

type InviteRole = "coach" | "spectator" | "caller";

/**
 * The owner's Invite over a share's people (components/InviteLink): a radio
 * button per role this share offers — a caller only where there is an agent
 * to ask — each saying what it may do in it.
 */
export function InvitePopover({ shareId, caps, made, onMade, ownerOnly }: {
  shareId: string;
  caps: Caps;
  made: MadeInvite[];
  onMade: (inv: MadeInvite) => void;
  /** an agent on its owner's subscription answers them alone: no caller is offered, and the note says why (byj-1wv.115.37) */
  ownerOnly?: boolean;
}) {
  const callers = caps.agent && !ownerOnly;
  const roles: InviteRole[] = callers ? ["caller", "spectator", "coach"] : ["spectator", "coach"];
  return (
    <InviteLinkPopover
      id={shareId}
      roles={roles.map((r) => ({ id: r, name: word("session:inviteRoleName", r)!, what: phrases.inviteRoleWhat(r, caps) }))}
      initial={{ role: roles[0]!, uses: 1, ttl: phrases.ttlOptions()[1]![0] }}
      create={(c) => api.createInvite(shareId, c.role as InviteRole, c.uses, c.ttl)}
      made={made}
      onMade={onMade}
      note={caps.agent && ownerOnly ? t("session:inviteOwnerOnly") : undefined}
    />
  );
}
